<template>
  <div v-if="computedAddressbook">
    <section-header
      id="addressbook"
      :departmentId="departmentId"
      :swiftMove="swiftMove"
      >Address book</section-header
    >
    <v-btn :to="computedAddEntryLink" small> <v-icon>add</v-icon>Entry </v-btn>
    <div class="addressbooks">
      <div
        class="addressbooks--group"
        v-for="typeGroup in computedAddressbook"
        :key="typeGroup.typeid"
      >
        <div class="addressbooks--group-write">
          <v-tooltip bottom v-if="typeGroup.write">
            <template v-slot:activator="{ on }">
              <v-btn :to="documentEntityLink(typeGroup)" v-on="on" small icon>
                <v-icon>create</v-icon>
              </v-btn>
            </template>
            <span>Compose document</span>
          </v-tooltip>
        </div>
        <div class="addressbooks--group-title">
          <span v-if="typeGroup.type">{{ typeGroup.type.name }}</span>
          <span v-if="!typeGroup.type">{{ typeGroup.typeid }}</span>
        </div>
        <div class="addressbooks--content">
          <div
            class="addressbook"
            v-for="addressbook in typeGroup.addressbooks"
            :key="addressbook.id"
          >
            <div class="addressbook--content">
              <EntityZ :entity="addressbook.entity" v-if="addressbook.entity" />
              <template v-if="typeGroup.type && typeGroup.type.employees">
                <v-btn :to="employeeAddLink(addressbook)" small>
                  <v-icon>add</v-icon>Employee
                </v-btn>
                <table v-if="addressbook.employees">
                  <tr
                    v-for="employee in addressbook.employees"
                    :key="employee.id"
                  >
                    <td>
                      <v-icon>account_circle</v-icon>
                    </td>
                    <td>
                      <EntityZ :entity="employee" />
                    </td>
                  </tr>
                </table>
              </template>
            </div>
            <div class="addressbook--references">
              <div
                class="addressbook--reference"
                v-for="(reference, i) in addressbook.references"
                :key="i"
              >
                <div class="addressbook--reference-prefix">
                  {{ reference.prefix }}
                </div>
                <div>{{ reference.reference }}</div>
                <v-tooltip bottom v-if="reference.prefix == 'Reference'">
                  <template v-slot:activator="{ on }">
                    <v-btn
                      v-clipboard:copy="reference.reference"
                      v-clipboard:success="onCopy"
                      v-clipboard:error="onError"
                      small
                      icon
                      v-on="on"
                    >
                      <v-icon>content_paste</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ `Copy ${reference.reference} to clipboard` }}</span>
                </v-tooltip>
              </div>
            </div>
            <div>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn
                    @click="deleteAddressbook(addressbook)"
                    v-if="addressbook.deletes"
                    v-on="on"
                    small
                    icon
                  >
                    <v-icon>delete</v-icon>
                  </v-btn>
                </template>
                <span>Remove entry</span>
              </v-tooltip>
            </div>
          </div>
        </div>
      </div>
      <div class="addressbooks--group" v-if="file.team">
        <div class="addressbooks--group-write" />
        <div class="addressbooks--group-title">Team</div>
        <div class="addressbook">
          <div class="addressbook--content">
            <Team :team="file.team" />
          </div>
        </div>
      </div>
      <div class="addressbooks--group" v-if="file.teamFuture">
        <div class="addressbooks--group-write" />
        <div class="addressbooks--group-title">Future team</div>
        <div class="addressbook">
          <div class="addressbook--content">
            <Team :team="file.teamFuture" />
          </div>
        </div>
      </div>
    </div>
    <v-snackbar
      v-model="addressbookSnackbar"
      :multi-line="true"
      :top="true"
      :timeout="3000"
    >
      {{ addressbookSnackbarText }}
      <v-btn color="pink" flat @click="addressbookSnackbar = false"
        >Close</v-btn
      >
    </v-snackbar>
  </div>
</template>

<script>
import FILE_ADDRESS_BOOKS from "@/graphql/file/FileAddressBooks";
import TYPES from "@/graphql/directory/Types";
import EntityZ from "@/components/link/EntityZ";
import SectionHeader from "@/components/file/SectionHeader";
import Team from "@/components/link/Team";
import nbmlaw from "@/api/nbmlaw";
export default {
  name: "Addressbook",
  apollo: {
    file: {
      query: FILE_ADDRESS_BOOKS,
      variables() {
        return {
          id: Number(this.$route.params.id)
        };
      },
      fetchPolicy: "cache-and-network"
    },
    types: {
      query: TYPES,
      fetchPolicy: "cache-and-network"
    }
  },
  components: {
    EntityZ,
    SectionHeader,
    Team
  },
  props: ["departmentId", "swiftMove"],
  computed: {
    computedAddressbook() {
      if (!this.computedGroups) {
        return undefined;
      }
      const clientGroups = this.computedGroups.filter(
        (g) => g.typeid == 3 || g.typeid == 4
      );
      const hasBoth = clientGroups.length == 2;
      const result = this.computedGroups.map((g) => {
        const allowMany = g.type ? g.type.allowMany : false;
        const isClient = g.typeid == 3 || g.typeid == 4;
        const clientCheck = isClient && hasBoth;
        const deletes = (allowMany && g.addressbooks.length > 1) || clientCheck;
        // const deletes = g.addressbooks.length > 1 || clientCheck;
        const addressbooks = g.addressbooks.map((a) => {
          return { ...a, deletes };
        });
        return { ...g, addressbooks };
      });
      return result;
    },
    computedGroups() {
      if (!this.computedAddressbooks) {
        return undefined;
      }
      let addressbook = this.computedAddressbooks.reduce((r, a) => {
        const typeid = a.entity ? a.entity.typeid : undefined;
        let current = r.find((e) => e.typeid == typeid);
        if (!current) {
          const type = this.types
            ? this.types.find((type) => type.id === typeid)
            : undefined;
          const write = this.writes ? this.writes.includes(typeid) : undefined;
          r.push({
            typeid,
            type,
            write,
            addressbooks: [a]
          });
        } else {
          current.addressbooks.push(a);
        }
        return r;
      }, []);
      addressbook.sort((a, b) => a.typeid - b.typeid);
      return addressbook;
    },
    computedAddressbooks() {
      if (!this.file || !this.file.addressBooks) {
        return undefined;
      }
      return this.file.addressBooks.map((a) => {
        const references = [];
        if (a.reference) {
          references.push({ prefix: "Reference", reference: a.reference });
        }
        if (a.entity) {
          if (a.entity.typeid == 5 && this.file.propertyPrice) {
            references.push({
              prefix: "Property price",
              reference: this.$options.filters.toCurrency(
                this.file.propertyPrice.body
              )
            });
          }
          if (a.entity.typeid == 8 && this.file.panel) {
            references.push({
              prefix: "Panel number",
              reference: this.file.panel.map((p) => p.panelno).join(",")
            });
          }
        }
        const employees = this.employees ? this.employees[a.id] : undefined;
        return { ...a, references, employees };
      });
    },
    computedAddEntryLink() {
      const relatedId =
        this.header && this.header.related ? this.header.related.id : undefined;
      return {
        name: "entryType",
        params: {
          objectType: "file",
          objectId: this.$route.params.id,
          relatedId
        }
      };
    }
  },
  data() {
    return {
      addressbook: null,
      writes: null,
      addressbookSnackbar: false,
      addressbookSnackbarText: null
    };
  },
  methods: {
    async fetchWrites() {
      const response = await nbmlaw.get(
        `/cors/admin/file/files/${this.$route.params.id}/writes`
      );
      // console.log(response.data);
      this.writes = response.data;
    },
    documentEntityLink(addressbookType) {
      return {
        name: "documentEntity",
        params: {
          objectType: "file",
          objectId: this.$route.params.id,
          entityTypeId: addressbookType.typeid
        }
      };
    },
    employeeAddLink(addressbook) {
      return {
        name: "employeeAdd",
        params: { id: addressbook.entityid },
        query: {
          addressbookid: addressbook.id
        }
      };
    },
    async deleteAddressbook(addressbook) {
      if (confirm("Are you sure?")) {
        await nbmlaw.delete(
          `/cors/admin/file/files/${this.$route.params.id}/addressbooks/${addressbook.id}`
        );
        // console.log(response.data);
        this.$apollo.queries.file.refetch();
        this.update();
      }
    },
    update() {
      this.fetchWrites();
    },
    onCopy() {
      this.addressbookSnackbar = true;
      this.addressbookSnackbarText = "Copied to clipboard";
    },
    onError() {
      this.addressbookSnackbar = true;
      this.addressbookSnackbarText = "Failed to copy texts";
    }
  },
  watch: {
    "$route.params.id": {
      immediate: true,
      handler() {
        this.update();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.addressbooks {
  display: flex;
  flex-direction: column;

  &--group {
    display: flex;
    flex-direction: row;
    padding: 0.5rem;
    align-items: center;

    &:nth-child(even) {
      background: #ddd;
    }

    &:nth-child(odd) {
      background: #fff;
    }

    &-write {
      width: 4rem;
    }

    &-title {
      text-transform: capitalize;
      font-weight: bold;
      width: 8rem;
    }
  }

  &--content {
    flex: 1;
    display: flex;
    flex-direction: column;
  }
}

.addressbook {
  display: flex;
  flex-direction: row;
  align-items: center;

  &--content {
    flex: 1;
  }

  &--references {
    display: flex;
    flex-direction: column;
  }

  &--reference {
    display: flex;
    flex-direction: row;
    align-items: center;

    &-prefix {
      font-size: 0.9rem;
      font-weight: bold;

      &:after {
        content: ": ";
        white-space: pre-wrap;
      }
    }
  }
}
</style>
